import React, { useState, useEffect, useMemo } from 'react';
import HostNav from '../components/hostComponents/HostNav';
import Property from '../components/hostComponents/Property';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getHostProperties, getHostEarnings, getAllOrders } from '../store/thunks/hostActions';
import { buildPropertyEarningsMap } from '../utils/earningsAggregation';

// ─── Condensed stats bar ─────────────────────────────────────────────────────
function PropertiesStatsBar({ properties }) {
  const total = properties?.length || 0;
  const active = properties?.filter(p => !p.isDeleted).length || 0;
  const pmsCount = (properties?.filter(p => p.propertySource === 'streamline').length ? 1 : 0) +
    (properties?.filter(p => p.propertySource === 'guesty').length ? 1 : 0);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 py-2.5 px-4 bg-white/70 backdrop-blur-xl rounded-xl border border-white/40 shadow-sm">
      <span className="text-sm text-gray-600">
        <span className="font-bold text-gray-900">{total}</span> total
      </span>
      <span className="text-gray-300">·</span>
      <span className="text-sm text-gray-600">
        <span className="font-bold text-gray-900">{active}</span> active
      </span>
      <span className="text-gray-300">·</span>
      <span className="text-sm text-gray-600">
        <span className="font-bold text-gray-900">{pmsCount}</span> PMS connected
      </span>
    </div>
  );
}

// ─── View toggle ─────────────────────────────────────────────────────────────
function ViewToggle({ viewMode, setViewMode }) {
  return (
    <div className="flex items-center bg-gray-100/60 backdrop-blur-sm rounded-xl p-1 border border-gray-200/40">
      <button
        onClick={() => setViewMode('list')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
      <button
        onClick={() => setViewMode('grid')}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
    </div>
  );
}

// ─── Property list row (list view) ───────────────────────────────────────────
function PropertyListRow({ property, propertyTotals }) {
  const navigate = useNavigate();
  const propertyEarnings = propertyTotals?.totalHostFee || 0;
  const orderCount = propertyTotals?.orderCount || 0;
  const formatAddress = (addr) => (!addr ? 'N/A' : [addr.line1, addr.city, addr.state].filter(Boolean).join(', '));

  return (
    <div
      onClick={() => navigate(`/properties/${property._id}`)}
      className="group flex items-center justify-between px-5 py-3.5 bg-white/70 backdrop-blur-xl border border-white/40 rounded-xl hover:shadow-md hover:bg-white/90 transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/20 flex-shrink-0 group-hover:scale-105 transition-transform">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">{property.listingTitle || 'Untitled Property'}</p>
          <p className="text-xs text-gray-500 truncate">{formatAddress(property.address)}</p>
        </div>
      </div>
      <div className="flex items-center space-x-6 flex-shrink-0">
        <div className="hidden sm:block text-right">
          <p className="text-xs text-gray-400 font-medium">Orders</p>
          <p className="text-sm font-bold text-gray-900">{orderCount}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 font-medium">Earned</p>
          <p className="text-sm font-bold text-green-600">${propertyEarnings.toFixed(2)}</p>
        </div>
        <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

// ─── Main Properties Screen ──────────────────────────────────────────────────
const PropertiesScreen = () => {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const { properties, loading: loadingProperties } = useSelector((state) => state.hostProperties);
  const { earnings } = useSelector((state) => state.hostEarnings);
  const { orders: allOrders } = useSelector((state) => state.orders);

  const [viewMode, setViewMode] = useState('list');
  const activePropertiesList = properties?.filter(p => !p.isDeleted) || [];
  const propertyEarningsMap = useMemo(
    () => buildPropertyEarningsMap({ orders: allOrders, earningsByProperty: earnings }),
    [allOrders, earnings]
  );

  // Transform earnings object into array format for Property component
  // Property component expects: [{ _id: 'propertyId', totalCommission: 123.45 }, ...]
  const earningsArray = useMemo(() => {
    if (!propertyEarningsMap || typeof propertyEarningsMap !== 'object') return [];
    return Object.keys(propertyEarningsMap).map((propertyId) => ({
      _id: propertyId,
      totalCommission: propertyEarningsMap[propertyId]?.totalHostFee || 0
    }));
  }, [propertyEarningsMap]);

  useEffect(() => {
    if (hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      dispatch(getAllOrders(hostInfo._id));
    }
  }, [hostInfo?._id, dispatch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 relative overflow-hidden">
      {/* Subtle Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-primary-500/5 to-purple-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-gradient-to-tr from-blue-500/5 to-primary-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-[300px] h-[300px] bg-gradient-to-r from-purple-500/3 to-pink-500/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <HostNav hide={true} />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
              <p className="text-gray-500 mt-1">Manage your properties and PMS integrations</p>
            </div>
            <Link
              to="/host"
              className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:-translate-y-0.5 font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Dashboard</span>
            </Link>
          </div>
        </div>

        {/* Condensed stats */}
        <PropertiesStatsBar properties={properties} />

        {/* Actions + view toggle */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
            <Link
              to="/add-property"
              className="flex items-center space-x-1.5 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-md shadow-primary-500/25 hover:shadow-lg text-sm font-medium hover:-translate-y-0.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Property</span>
            </Link>
            <Link
              to="/integrations"
              className="flex items-center space-x-1.5 px-4 py-2 bg-white/80 border border-gray-200/60 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 text-sm font-medium"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span>Integrations</span>
            </Link>
          </div>
        </div>

        {/* Property list / grid */}
        {loadingProperties ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm p-6 animate-pulse border border-white/40">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : activePropertiesList.length === 0 ? (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-12 text-center border border-white/40 shadow-sm">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary-500/25">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">No Properties Yet</h3>
              <p className="text-gray-500 text-sm mb-5">Add a property or connect Guesty/Streamline to sync.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/add-property"
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 font-medium text-sm"
                >
                  Add Property
                </Link>
                <Link
                  to="/integrations"
                  className="inline-flex items-center px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm"
                >
                  Integrations
                </Link>
              </div>
            </div>
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2">
            {activePropertiesList.map((property) => (
              <PropertyListRow key={property._id} property={property} propertyTotals={propertyEarningsMap?.[property._id]} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activePropertiesList.map((property) => (
              <Property key={property._id} property={property} data={earningsArray} rawDataList={[]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesScreen;
